import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent } from 'effector-react';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { ToastType, useToast } from '@nx-frontend/shared/ui/toast/use-toast';
import { axiosClient } from '@nx-frontend/shared/api';
import { MenuItemType, PopupType } from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';

interface UnshareNoteInfo {
  globalId: string;
  workspaceId: string;
  title: string;
  type: MenuItemType;
}

/**
 * @deprecated Use `useUnshareNote` instead
 */
export function unshareNote(
  openModal: (modalState: OpenModalParams) => void,
  closeModal: () => void,
  toast: (params: ToastType) => void,
  note: UnshareNoteInfo
) {
  const { workspaceId, globalId, type } = note;
  return openModal({
    title: 'Unshare ' + (type === MenuItemType.NOTES_FOLDER ? 'page' : 'folder'),
    content: PopupType.DIALOG,
    showCloseButton: true,
    closeButtonSize: 18,
    payload: {
      payloadType: PopupType.DIALOG,
      text: 'Are you sure you want to unshare this ' + (type === MenuItemType.NOTES_FOLDER ? 'page' : 'folder') + '?',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Unshare',
          main: true,
          onClick: () => {
            closeModal();
            axiosClient.delete(`/v2/api/workspaces/${workspaceId}/notes/${globalId}/share`).then(() => {
              toast({ title: 'Public sharing has been removed' });
            });
          },
        },
      ],
    },
  });
}

export function useUnshareNote() {
  const [openModal, closeModal] = useEvent([openModalEvent, closeModalEvent]);
  const { toast } = useToast();
  const t = useTranslations('webnotes');

  return useCallback(
    (note: UnshareNoteInfo) => {
      const { workspaceId, globalId, type } = note;
      return openModal({
        title: type === MenuItemType.NOTES_FOLDER ? t('webnotes-unshare_page') : t('webnotes-unshare_folder'),
        content: PopupType.DIALOG,
        showCloseButton: true,
        closeButtonSize: 18,
        payload: {
          payloadType: PopupType.DIALOG,
          text:
            type === MenuItemType.NOTES_FOLDER
              ? t('webnotes-unshare_page_description')
              : t('webnotes-unshare_folder_description'),
          buttons: [
            {
              text: t('webnotes-cancel'),
            },
            {
              text: t('webnotes-unshare'),
              main: true,
              onClick: () => {
                closeModal();
                axiosClient.delete(`/v2/api/workspaces/${workspaceId}/notes/${globalId}/share`).then(() => {
                  toast({ title: t('webnotes-unshare_success_toast') });
                });
              },
            },
          ],
        },
      });
    },
    [openModal, closeModal, toast]
  );
}
